import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Logo from '../../components/Logo';
import SEO from '../../components/SEO';
import { Target, Phone, Mail, Linkedin, Database, BarChart3, Calendar, Users, Zap, Settings, ClipboardCheck, ChevronRight, Check, X } from 'lucide-react';

// Hook pour animations au scroll
const useInView = () => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
      }
    }, { threshold: 0.1 });

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return [ref, isInView];
};

// Composant pour sections animées
const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const [ref, isInView] = useInView();
  
  return (
    <div 
      ref={ref}
      className={`transform transition-all duration-700 ${className}`}
      style={{ 
        transitionDelay: `${delay}ms`,
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(30px)'
      }}
    >
      {children}
    </div>
  );
};

const Accelerateur = () => {
  const [openFaq, setOpenFaq] = useState(null);

  // SEO Configuration
  const seoConfig = {
    title: 'Prospection Externalisée & Accélération Business - FOMOW | Génération Leads B2B',
    description: 'Prospection externalisée et génération de RDV qualifiés B2B. Cold call, emailing, LinkedIn. 20-30 rendez-vous qualifiés par mois. Nous appelons, vous closez.',
    path: '/services/accelerateur',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Accélération & Scaling Business",
      "description": "Prospection externalisée et génération de leads B2B qualifiés",
      "provider": { "@type": "Organization", "name": "FOMOW" },
      "serviceType": "Lead Generation"
    }
  };

  // Canaux de prospection
  const channels = [
    { 
      icon: Phone, 
      title: 'Cold Call', 
      desc: 'Appels sortants vers vos cibles décisionnaires. Qualification immédiate et prise de RDV.',
    },
    { 
      icon: Mail, 
      title: 'Cold Email', 
      desc: 'Séquences multicanales personnalisées. Copywriting optimisé pour maximiser les réponses.',
    },
    { 
      icon: Linkedin, 
      title: 'LinkedIn', 
      desc: 'Social selling et approche directe. Ciblage précis grâce à Sales Navigator.',
    },
  ];

  // Méthode étape par étape
  const methodology = [
    { 
      num: '01',
      title: 'Cadrage stratégique', 
      desc: 'Définition de votre ICP, personas et messages clés.',
      icon: Target,
      details: ['Analyse de votre marché cible', 'Construction du pitch adapté', 'Définition des objectifs']
    },
    { 
      num: '02',
      title: 'Construction des listes', 
      desc: 'Sourcing, enrichissement et qualification de vos prospects.',
      icon: Database,
      details: ['Scraping et data enrichment', 'Vérification des contacts', 'Segmentation avancée']
    },
    { 
      num: '03',
      title: 'Exécution des campagnes', 
      desc: 'Déploiement multicanal avec suivi en temps réel.',
      icon: Zap,
      details: ['Séquences personnalisées', 'A/B testing messages', 'Ajustements continus']
    },
    { 
      num: '04',
      title: 'Livraison de RDV', 
      desc: 'Rendez-vous qualifiés directement dans votre agenda.',
      icon: Calendar,
      details: ['RDV confirmés avec décideurs', 'Brief avant chaque meeting', 'Intégration CRM']
    },
  ];

  // Ce qui nous différencie
  const differentiators = [
    {
      icon: Users,
      title: 'Équipe 100% France',
      desc: 'Des commerciaux expérimentés basés en France. Aucune sous-traitance à l\'étranger.',
    },
    {
      icon: BarChart3,
      title: 'Transparence totale',
      desc: 'Accès complet au pipeline, aux listes et aux échanges. Reporting hebdomadaire.',
    },
    {
      icon: ClipboardCheck,
      title: 'RDV vraiment qualifiés',
      desc: 'Prospects correspondant à votre ICP, intéressés et disponibles pour un échange.',
    },
    {
      icon: Settings,
      title: 'Approche sur-mesure',
      desc: 'Stratégie adaptée à votre secteur, votre cycle de vente et vos objectifs.',
    },
  ];

  // Pour qui
  const forWho = [
    {
      title: 'Dirigeants qui vendent',
      desc: 'Fondateurs, CEO ou DG qui portent le développement commercial et manquent de temps pour prospecter.',
      ideal: 'Libérez-vous pour vous concentrer sur les RDV et le closing.',
    },
    {
      title: 'Équipes commerciales',
      desc: 'Sales teams qui ont besoin d\'un flux constant de leads qualifiés pour atteindre leurs objectifs.',
      ideal: 'Alimentez votre pipeline sans effort de prospection.',
    },
    {
      title: 'Scale-ups en croissance',
      desc: 'Entreprises qui veulent accélérer leur acquisition sans recruter immédiatement.',
      ideal: 'Scalez votre acquisition rapidement et de manière flexible.',
    },
  ];

  // Modèle de suivi
  const followUp = [
    {
      freq: 'Kick-off',
      title: 'Lancement',
      desc: 'Définition de la stratégie, validation des messages et construction des premières listes.',
    },
    {
      freq: 'Hebdomadaire',
      title: 'Reporting',
      desc: 'Notification en temps réel des RDV générés. Reporting détaillé des actions et résultats.',
    },
    {
      freq: 'Mensuel',
      title: 'Pilotage',
      desc: 'Analyse des performances, ajustement de la stratégie et définition des objectifs du mois suivant.',
    },
  ];

  // Comparaison
  const comparisons = [
    { bad: 'Call centers offshore bas de gamme', good: 'Commerciaux seniors basés en France' },
    { bad: 'Scripts génériques sans personnalisation', good: 'Messages adaptés à chaque segment et persona' },
    { bad: 'Volume sans qualité', good: 'RDV qualifiés avec décideurs intéressés' },
    { bad: 'Reporting opaque et occasionnel', good: 'Transparence totale, accès au pipeline en temps réel' },
  ];

  // FAQ
  const faqs = [
    { 
      q: 'Combien de RDV puis-je attendre par mois ?', 
      a: 'En moyenne, nos clients obtiennent entre 15 et 30 RDV qualifiés par mois selon leur secteur et la taille de leur marché adressable. Les premiers RDV arrivent généralement dès la deuxième semaine.' 
    },
    { 
      q: 'Comment définissez-vous un RDV qualifié ?', 
      a: 'Un RDV est qualifié quand le prospect correspond à votre ICP, occupe un poste décisionnaire, a exprimé un intérêt pour échanger et a confirmé sa disponibilité. On vous fournit un brief avant chaque meeting.' 
    },
    { 
      q: 'Quels canaux de prospection utilisez-vous ?', 
      a: 'Prospection multicanale : cold call, cold email et LinkedIn. On adapte le mix selon votre cible, votre secteur et ce qui fonctionne le mieux. Le cold call reste notre canal principal pour la prise de RDV.' 
    },
    { 
      q: 'Quel est le délai pour démarrer ?', 
      a: 'On peut lancer les premières campagnes sous 7 à 10 jours après le kick-off. Ce délai inclut la définition de la stratégie, la construction des listes et la validation des messages.' 
    },
    { 
      q: 'Comment s\'intègre votre travail avec notre CRM ?', 
      a: 'Nous nous adaptons à vos outils. Les RDV et les informations prospects sont intégrés directement dans votre CRM (HubSpot, Salesforce, Pipedrive...) ou via un fichier partagé si vous préférez.' 
    },
  ];

  return (
    <div className="bg-[#F5F5F5] min-h-screen">
      <SEO 
        title={seoConfig.title}
        description={seoConfig.description}
        path={seoConfig.path}
        structuredData={seoConfig.structuredData}
      />
      <Navbar />
      
      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 bg-gradient-to-b from-[#041C2C] to-[#0a2a3d] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-10 w-96 h-96 bg-[#2A41E5]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-64 h-64 bg-[#2A41E5]/5 rounded-full blur-2xl" />
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <AnimatedSection>
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#2A41E5]/20 border border-[#2A41E5]/30 mb-6">
              <span className="text-sm text-[#2A41E5] font-medium">Prospection externalisée</span>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={100}>
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Accélération & Scaling Business
            </h1>
          </AnimatedSection>
          
          <AnimatedSection delay={200}>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl leading-relaxed mb-4">
              <span className="text-[#2A41E5] font-semibold">Nous prospectons, vous vendez.</span>{' '}
              Générez des rendez-vous qualifiés avec vos décideurs cibles.
            </p>
          </AnimatedSection>
          
          <AnimatedSection delay={300}>
            <p className="text-lg text-white/60 max-w-2xl mb-10">
              Une équipe commerciale expérimentée dédiée à votre acquisition. 
              Concentrez-vous sur ce que vous faites de mieux : convaincre et closer.
            </p>
          </AnimatedSection>
          
          <AnimatedSection delay={400}>
            <a
              href="https://meetings-eu1.hubspot.com/younes8" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#2A41E5] text-white font-bold rounded-xl hover:bg-[#1E32B8] transition-all duration-300 hover:shadow-lg hover:shadow-[#2A41E5]/30 group"
            >
              Contactez-nous
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* Canaux de prospection */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">Nos canaux</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              Une approche multicanale pour maximiser l'impact
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              Cold call, email, LinkedIn : on combine les canaux pour atteindre vos cibles là où elles sont.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-3 gap-8">
            {channels.map((channel, idx) => {
              const IconComponent = channel.icon;
              return (
                <AnimatedSection key={idx} delay={idx * 150}>
                  <div className="bg-[#F5F5F5] rounded-2xl p-8 h-full border border-gray-100 hover:border-[#2A41E5]/30 hover:shadow-lg transition-all duration-300 text-center group">
                    <div className="flex items-center justify-center mx-auto mb-6">
                      <IconComponent className="w-10 h-10 text-[#2A41E5]" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{channel.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{channel.desc}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Méthode */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#041C2C]">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">Notre méthode</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-white mb-4 text-center">
              Du ciblage au rendez-vous : un process éprouvé
            </h2>
            <p className="text-lg text-white/60 text-center max-w-3xl mx-auto mb-16">
              Une approche structurée pour transformer vos cibles en opportunités commerciales.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-2 gap-8">
            {methodology.map((step, idx) => {
              const IconComponent = step.icon;
              return (
                <AnimatedSection key={idx} delay={idx * 150}>
                  <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:bg-white/10 hover:border-[#2A41E5]/50 transition-all duration-300 group h-full">
                    <div className="flex items-start gap-6">
                      <div className="w-14 h-14 rounded-xl bg-[#2A41E5] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <span className="text-white font-bold text-lg">{step.num}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <IconComponent className="w-5 h-5 text-[#2A41E5]" />
                          <h3 className="text-xl font-bold text-white">{step.title}</h3>
                        </div>
                        <p className="text-white/70 leading-relaxed mb-4">{step.desc}</p>
                        <ul className="space-y-2">
                          {step.details.map((detail, i) => (
                            <li key={i} className="flex items-center gap-2 text-white/60 text-sm">
                              <div className="w-1.5 h-1.5 rounded-full bg-[#2A41E5]" />
                              {detail}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ce qui nous différencie */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">Nos engagements</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              Pas une agence de téléprospection classique
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              Notre approche est fondée sur la qualité, la transparence et l'expertise métier.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {differentiators.map((diff, idx) => {
              const IconComponent = diff.icon;
              return (
                <AnimatedSection key={idx} delay={idx * 100}>
                  <div className="bg-[#F5F5F5] rounded-2xl p-6 h-full border border-gray-100 hover:border-[#2A41E5]/30 hover:shadow-lg transition-all duration-300 text-center">
                    <div className="flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-8 h-8 text-[#2A41E5]" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{diff.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{diff.desc}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pour qui */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#F5F5F5]">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">Pour qui</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              À qui s'adresse cet accompagnement
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              Dirigeants, équipes sales ou scale-ups : déléguez votre prospection à des experts.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-3 gap-8">
            {forWho.map((profile, idx) => (
              <AnimatedSection key={idx} delay={idx * 150}>
                <div className="bg-white rounded-2xl p-8 h-full border border-gray-100 hover:border-[#2A41E5]/30 hover:shadow-lg transition-all duration-300">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{profile.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">{profile.desc}</p>
                  <div className="pt-4 border-t border-gray-100">
                    <p className="text-sm text-[#2A41E5] font-medium">{profile.ideal}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Modèle de suivi */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">Suivi continu</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              Un pilotage transparent de la performance
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              On vous tient informé à chaque étape. Pas de boîte noire.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-3 gap-8">
            {followUp.map((item, idx) => (
              <AnimatedSection key={idx} delay={idx * 150}>
                <div className="text-center">
                  <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#2A41E5] text-white text-sm font-medium mb-6">
                    {item.freq}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Comparaison */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#F5F5F5]">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              La différence avec les autres
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              Qualité avant volume. Transparence avant opacité. Résultats avant promesses.
            </p>
          </AnimatedSection>
          
          <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
            <div className="grid md:grid-cols-2">
              {/* Ce qu'on NE fait PAS */}
              <div className="p-8 md:p-10 bg-gray-50">
                <h3 className="text-xl font-bold text-gray-400 mb-6 flex items-center gap-2">
                  <X className="w-6 h-6 text-red-400" />
                  Agences classiques
                </h3>
                <ul className="space-y-4">
                  {comparisons.map((comp, idx) => (
                    <AnimatedSection key={idx} delay={idx * 100}>
                      <li className="flex items-start gap-3 text-gray-500">
                        <span className="text-red-400 mt-1">✗</span>
                        {comp.bad}
                      </li>
                    </AnimatedSection>
                  ))}
                </ul>
              </div>
              
              {/* Ce qu'on FAIT */}
              <div className="p-8 md:p-10 bg-[#041C2C]">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <Check className="w-6 h-6 text-green-400" />
                  Notre approche
                </h3>
                <ul className="space-y-4">
                  {comparisons.map((comp, idx) => (
                    <AnimatedSection key={idx} delay={idx * 100}>
                      <li className="flex items-start gap-3 text-white/80">
                        <span className="text-green-400 mt-1">✓</span>
                        {comp.good}
                      </li>
                    </AnimatedSection>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <AnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-12 text-center">
              Questions fréquentes
            </h2>
          </AnimatedSection>
          
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <AnimatedSection key={idx} delay={idx * 100}>
                <div className="bg-[#F5F5F5] rounded-2xl border border-gray-200 overflow-hidden hover:border-[#2A41E5]/30 transition-colors">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                    <svg 
                      className={`w-5 h-5 text-[#2A41E5] transition-transform flex-shrink-0 ${openFaq === idx ? 'rotate-180' : ''}`} 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openFaq === idx ? 'max-h-96' : 'max-h-0'}`}>
                    <div className="px-6 pb-6">
                      <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#041C2C]">
        <div className="max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-white mb-6">
              Prêt à accélérer votre développement commercial ?
            </h2>
            <p className="text-lg text-white/60 mb-10 max-w-2xl mx-auto">
              Discutons de vos objectifs d'acquisition. On vous explique comment on peut remplir votre pipeline.<br />
              Sans engagement.
            </p>
            <a
              href="https://meetings-eu1.hubspot.com/younes8" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#2A41E5] text-white font-bold rounded-xl hover:bg-[#1E32B8] transition-all duration-300 hover:shadow-lg hover:shadow-[#2A41E5]/30 group"
            >
              Réserver un échange
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* Services liés */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#F5F5F5] to-white">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-12">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider mb-3 block">
                Découvrez aussi
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">Nos autres accompagnements</h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Link 
                to="/services/architecte" 
                className="group relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#2A41E5]/20 shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden text-center"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#2A41E5]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <h4 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#2A41E5] transition-colors">Ingénierie & Systèmes Commerciaux</h4>
                  <p className="text-gray-600 leading-relaxed mb-4">Conception de votre architecture commerciale : équipes, process, outils et playbooks sur-mesure.</p>
                  <div className="inline-flex items-center gap-2 text-[#2A41E5] font-medium group-hover:gap-3 transition-all">
                    <span>Découvrir</span>
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
              
              <Link 
                to="/services/pilote" 
                className="group relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#2A41E5]/20 shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden text-center"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#2A41E5]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <h4 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#2A41E5] transition-colors">Direction Commerciale Externalisée</h4>
                  <p className="text-gray-600 leading-relaxed mb-4">Des leaders commerciaux intégrés à votre équipe pour exécuter et piloter.</p>
                  <div className="inline-flex items-center gap-2 text-[#2A41E5] font-medium group-hover:gap-3 transition-all">
                    <span>Découvrir</span>
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 sm:py-16 px-4 sm:px-6 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto flex flex-col items-center justify-center gap-6">
          <Logo variant="white" height={28} />
          <p className="text-gray-400">&copy; 2024 FOMOW. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default Accelerateur;
